import React, { useCallback, useEffect, useState } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { addNewAdherent } from "../../../services/adherentServices";
import { getSettings } from "../../../services/settingsServices";
import { createDossierInscription } from "./../../../services/dossierServices";
import { toast } from "react-toastify";

const initialState = {
  nom: "",
  prenom: "",
  email: "",
  email_secondaire: "",
  tel: "",
  tel_secondaire: "",
  representant_legal: "",
  adresse: "",
  cp: "",
  commune: "",
  genre: "",
  statut_inscription: "Inscrit",
  date_naissance: "",
  lieu_naissance: "",
  photo_url: "",
  licence_number: "",
  poids: "",
  taille_tshirt: "",
  saison_active: "",
  notes_adherent: "",
  autorisation_photo: false,
  passsport_number: "",
  nationalite_fra: true,
};

export default function CreateAdherentAdminModal({
  isOpen,
  onClose,
  refreshList,
}) {
  const [formData, setFormData] = useState(initialState);
  const [seasons, setSeasons] = useState([]);
  const [imgOk, setImgOk] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const getPreviousSeason = (currentSeason = "") => {
    const [y1, y2] = currentSeason.split("-").map(Number);
    if (!y1 || !y2) return "";
    return `${y1 - 1}-${y2 - 1}`;
  };
  const getNextSeason = (currentSeason = "") => {
    const [y1, y2] = currentSeason.split("-").map(Number);
    if (!y1 || !y2) return "";
    return `${y1 + 1}-${y2 + 1}`;
  };

  const loadSeasons = useCallback(async () => {
    try {
      const settingsData = await getSettings();
      const active = settingsData?.[0]?.saison_active || "";
      const prev = getPreviousSeason(active);
      const next = getNextSeason(active);
      const opts = [prev, active, next].filter(Boolean);
      setSeasons(opts);
      // Pré-remplir la saison si vide
      setFormData((p) => ({
        ...p,
        saison_active: p.saison_active || active || "",
      }));
    } catch (e) {
      console.error("Erreur chargement settings:", e);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    // reset propre à chaque ouverture
    setFormData((p) => ({
      ...initialState,
      saison_active: "", // sera défini dans loadSeasons()
    }));
    setImgOk(true);
    loadSeasons();
  }, [isOpen, loadSeasons]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const canSubmit =
    formData.nom.trim() !== "" &&
    formData.prenom.trim() !== "" &&
    (formData.saison_active || "") !== "" &&
    !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      // 1) Créer l’adhérent
      const newAdherent = await addNewAdherent(formData);

      if (!newAdherent || !newAdherent.id) {
        throw new Error("Création adhérent: pas d'ID retourné");
      }

      // 2) Créer (ou récupérer) le dossier d'inscription côté serveur
      // --- si tu as adopté la signature (adherentId, extra) ---
      await createDossierInscription(newAdherent.id);
      // --- sinon, si ton service attend un payload brut ---
      // await createDossierInscription({ inscription_adherent_id: newAdherent.id });

      toast.success("Adhérent + dossier d'inscription créés avec succès");

      // 3) UI refresh & fermeture
      refreshList?.();
      onClose?.();
      setFormData(initialState);
    } catch (error) {
      console.error("Erreur lors de la création adhérent/dossier:", error);
      toast.error("Erreur lors de la création de l'adhérent ou du dossier");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      {/* Modal */}
      <div className="relative w-full max-w-4xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Créer un adhérent
          </h3>
          <p className="text-white/90 text-center text-xs mt-1">
            Renseignez les informations ci-dessous. Les champs Nom, Prénom et
            Saison sont obligatoires.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Contenu scrollable */}
          <div className="p-5 sm:p-6 space-y-6 max-h-[75vh] overflow-y-auto">
            {/* ÉTAT CIVIL */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
              <div className="px-4 py-3 border-b border-slate-200/70 dark:border-slate-800/70">
                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  État civil
                </h4>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Nom / Prénom / Genre / Date / Lieu / Licence / Poids */}
                <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Nom *
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Genre
                    </label>
                    <select
                      name="genre"
                      value={formData.genre}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    >
                      <option value="">— Sélectionner —</option>
                      <option value="Masculin">Masculin</option>
                      <option value="Féminin">Féminin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Date de naissance
                    </label>
                    <input
                      type="date"
                      name="date_naissance"
                      value={formData.date_naissance}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Lieu de naissance
                    </label>
                    <input
                      type="text"
                      name="lieu_naissance"
                      value={formData.lieu_naissance}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Numéro de licence
                    </label>
                    <input
                      type="text"
                      name="licence_number"
                      value={formData.licence_number}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Poids (kg)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      name="poids"
                      value={formData.poids}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Taille du T-shirt
                    </label>
                    <input
                      type="text"
                      name="taille_tshirt"
                      value={formData.taille_tshirt}
                      onChange={handleChange}
                      placeholder="ex: S, M, L, XL, XXL, XXXL…"
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    />
                  </div>
                </div>

                {/* Photo URL + Preview */}
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium mb-1">
                    URL de la photo
                  </label>
                  <input
                    type="url"
                    name="photo_url"
                    value={formData.photo_url}
                    onChange={(e) => {
                      setImgOk(true);
                      handleChange(e);
                    }}
                    placeholder="https://…"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                  <div className="mt-3 flex items-center justify-center">
                    <div className="h-28 w-28 rounded-xl ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      {formData.photo_url ? (
                        <img
                          src={formData.photo_url}
                          alt="Aperçu"
                          onError={() => setImgOk(false)}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-xs text-slate-500 px-2 text-center">
                          Aucun visuel
                        </span>
                      )}
                    </div>
                  </div>
                  {!imgOk && (
                    <div className="mt-2 text-xs text-rose-600">
                      Impossible de charger l’image.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* COORDONNÉES */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
              <div className="px-4 py-3 border-b border-slate-200/70 dark:border-slate-800/70">
                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Coordonnées
                </h4>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Email secondaire
                  </label>
                  <input
                    type="email"
                    name="email_secondaire"
                    value={formData.email_secondaire}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Téléphone
                  </label>
                  <input
                    type="text"
                    name="tel"
                    value={formData.tel}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Téléphone secondaire
                  </label>
                  <input
                    type="text"
                    name="tel_secondaire"
                    value={formData.tel_secondaire}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Représentant légal
                  </label>
                  <input
                    type="text"
                    name="representant_legal"
                    value={formData.representant_legal}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Adresse
                  </label>
                  <input
                    type="text"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Code postal
                  </label>
                  <input
                    type="text"
                    name="cp"
                    value={formData.cp}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Commune
                  </label>
                  <input
                    type="text"
                    name="commune"
                    value={formData.commune}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
              </div>
            </div>

            {/* INFOS CLUB */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/40">
              <div className="px-4 py-3 border-b border-slate-200/70 dark:border-slate-800/70">
                <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Infos club
                </h4>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Statut d'inscription
                  </label>
                  <select
                    name="statut_inscription"
                    value={formData.statut_inscription}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  >
                    <option value="Inscrit">Inscrit</option>
                    <option value="Licencié">Licencié</option>
                    <option value="Cours d'essai">Cours d'essai</option>
                    <option value="Licencié - Arrêt">Licencié - Arrêt</option>
                    <option value="Non Licencié - Arrêt">
                      Non Licencié - Arrêt
                    </option>
                    <option value="Licencié Hors Club">
                      Licencié Hors Club
                    </option>
                    <option value="Ancien Licencié">Ancien Licencié</option>
                    <option value="Ancien Licencié et Arrêt">
                      Ancien Licencié et Arrêt
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Saison active *
                  </label>
                  <select
                    name="saison_active"
                    value={formData.saison_active}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  >
                    <option value="">— Sélectionner —</option>
                    {seasons.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    N° Pass’Sport (facultatif)
                  </label>
                  <input
                    type="text"
                    name="passsport_number"
                    value={formData.passsport_number}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes_adherent"
                    value={formData.notes_adherent}
                    onChange={handleChange}
                    rows={4}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="autorisation_photo"
                      checked={formData.autorisation_photo}
                      onChange={handleChange}
                      className="h-5 w-5"
                    />
                    <span className="text-sm">Autorisation photo</span>
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="nationalite_fra"
                      checked={formData.nationalite_fra}
                      onChange={handleChange}
                      className="h-5 w-5"
                    />
                    <span className="text-sm">Nationalité française</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div className="px-5 pb-5 sm:px-6 sm:pb-6 flex justify-end gap-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              disabled={!canSubmit}
              className={`!rounded-xl gap-2 ${
                !canSubmit ? "opacity-60 pointer-events-none" : ""
              }`}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
